import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import useItem from 'common/hooks/useItem';
import ImageFromStrapiMedia from 'common/components/ImageFromStrapiMedia/ImageFromStrapiMedia';
import useRouter from 'common/hooks/use-router';
import DetailHeader from 'common/components/DetailHeader/DetailHeader';
import HeaderButtons from 'common/components/DetailHeader/HeaderButtons';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import PageLoading from 'common/components/PageLoading/PageLoading';
import ImageComponent from 'common/components/ImageComponent/ImageComponent';
import Icon from 'components/Icon/Icon';
import Status from 'common/components/Status/Status';
import AlertsContext from 'common/providers/alerts';
import { Content } from 'types/Content';
import fetchJSON from 'common/utils/fetchJSON';
import Modal from 'common/components/Modal/Modal';
import ProfileCard from 'components/ProfileCard/ProfileCard';
import { Gallery, Item } from 'react-photoswipe-gallery';
import 'photoswipe/dist/photoswipe.css';
import useAuth from 'common/hooks/useAuth';

function ContentDetails() {
  const router = useRouter();
  const id = router.query.id as string;
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const [fullScreenImg, setFullScreenImg] = useState<any | null>(null);
  const { user } = useAuth();

  const { item, fetchItem, isFetching, isUpdateFetching, error }: any = useItem('contents', id, {
    populate: ['category', 'authorOperator', 'author', 'images', 'documents'],
  });

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const {
    title,
    description,
    publishedDate,
    endPublishedDate,
    images,
    status,
    authorOperator,
    category,
    updatedAt,
    documents,
  } = (item as Content) || {};

  const canPublish = status === 'draft' && user?.isAdmin;
  const canUnpublish = status === 'published' && user?.isAdmin;
  const canArchive = ['draft', 'published'].includes(status) && user?.isAdmin;

  const contentAction = useCallback(
    async (action: string) => {
      const res = await fetchJSON({
        method: 'POST',
        url: `news/data/${action}/${id}`,
      });
      if (res) {
        fetchItem();
      }
    },
    [fetchItem, id]
  );

  const handlePublish = useCallback(() => {
    contentAction('publish');
  }, [contentAction]);

  const handleUnpublish = useCallback(() => {
    contentAction('unpublish');
  }, [contentAction]);

  const handleArchive = useCallback(() => {
    contentAction('archive');
  }, [contentAction]);

  const PublicationHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center text-xs gap-1 field-title">
          <Icon name="ClockIcon" className="w-4 h-4" />
          {dayjs(updatedAt).fromNow()}
        </div>
        <div className="responsive-flex items-center gap-3">
          <div className="flex flex-row items-center text-ternary-800">
            <div className="text-sm flex">
              <div className="flex items-center">
                <span>
                  {endPublishedDate
                    ? t('reports.publishedFromTo', {
                        from: dayjs(publishedDate).format('LL'),
                        to: dayjs(endPublishedDate).format('LL'),
                      })
                    : t('forms.notPublishedYet')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    [endPublishedDate, publishedDate, t, updatedAt]
  );

  const PublicationHeaderSubTitle = useCallback(
    (mainText: string, subText?: string) => (
      <>
        <span className="font-bold">{mainText}</span>
        {subText && <span> | {subText}</span>}
      </>
    ),
    []
  );

  const getIndexRelativeImage = useCallback(
    (currentImage: any, indexVariation: number) => {
      let iImg = 0;
      images.forEach((image, i) => {
        if (image.url === currentImage?.url) {
          iImg = i;
        }
      });
      if (iImg + indexVariation < 0) {
        return images[images.length - 1];
      }
      if (iImg + indexVariation >= images.length) {
        return images[0];
      }
      return images[iImg + indexVariation];
    },
    [images]
  );

  const handleAuthorClick = () => {
    if (authorOperator?.id) {
      router.navigate(`/mediators/edit/${authorOperator.id}`);
    }
  };

  if (isFetching && !item) {
    return <PageLoading />;
  }

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500 text-base">
      <Modal
        title={fullScreenImg?.name}
        visible={!!fullScreenImg}
        onCloseModal={() => setFullScreenImg(null)}
        showCloseModalFooter
        maxWindowSize
      >
        <div className="flex flex-row gap-0 justify-center items-stretch">
          <div
            className="flex flex-col justify-center cursor-pointer text-slate-600 hover:text-black hover:pt-1"
            onClick={() => setFullScreenImg(getIndexRelativeImage(fullScreenImg, -1))}
          >
            <Icon name="ArrowLeftIcon" className="w-12 h-12" />
          </div>
          <div className="rounded-2xl overflow-hidden opacity-100 bg-white">
            <ImageComponent image={ImageFromStrapiMedia(fullScreenImg)?.uri} />
          </div>
          <div
            className="flex flex-col justify-center cursor-pointer text-slate-600 hover:text-black hover:pt-1"
            onClick={() => setFullScreenImg(getIndexRelativeImage(fullScreenImg, 1))}
          >
            <Icon name="ArrowRightIcon" className="w-12 h-12" />
          </div>
        </div>
      </Modal>
      <DetailHeader
        backLink="/contents"
        title={title}
        Status={
          <div className="flex gap-2 flex-wrap">
            <Status status={status} />
          </div>
        }
        subtitleIcon="WorkSiteIcon"
        HeaderInfos={PublicationHeaderInfos}
        SubTitle={PublicationHeaderSubTitle(category?.name)}
        HeaderButtons={
          <HeaderButtons
            editUrl={`/contents/edit/${id}`}
            editDisabled={
              !user?.isAdmin &&
              !user?.isSuperAdmin &&
              (user?.id !== item?.author?.id || item?.status !== 'draft')
            }
            // validate or reject
            onValidate={handlePublish}
            validateDisabled={!canPublish}
            validateLabelKey="actions.publish"
            // unpublish
            onUnpublish={handleUnpublish}
            unpublishDisabled={!canUnpublish}
            // archive
            onArchive={handleArchive}
            archiveDisabled={!canArchive}
            // TODO : ne sert plus à rien puisque géré par des routes customs (!=update)
            // Il faudrait intégrer les fetchJSON dans un provider pour gérer les chargements de changement de statut
            isPublishing={isUpdateFetching('updateStatus')}
          />
        }
      />

      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {status === 'draft' && publishedDate && (
              <div className="w-full py-2 px-1">
                <div className="px-4 py-2 w-full flex-col font-semibold text-md leading-4 bg-white text-slate-800 border border-slate-300 rounded-md shadow-sm ring-2 ring-offset-slate-50 ring-pink-500 ring-offset-0">
                  <div className="flex flex-row items-center">
                    <Icon name="ExclamationTriangleIcon" className="w-4 h-4 mr-1" />
                    <span className="font-bold">
                      {t('infos.alreadyPublished', {
                        date: dayjs(publishedDate).format('LLL'),
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {/* Description */}
            <div className="responsive-flex flex flex-col xl:flex-row gap-2 items-start">
              {/* Column 1 */}
              <div className="white-box responsive-flex gap-2 flex-col w-full xl:w-2/3">
                <SectionTitle
                  title={t('forms.description')}
                  icon="BookmarkIcon"
                  className="text-fourth mt-2"
                />
                <div className="responsive-flex py-2 px-4 flex-col border rounded">
                  {description ? (
                    description.split('\n').map((p: any, index: number) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <p key={`p-${index}`} className="mb-4">
                        {p}
                      </p>
                    ))
                  ) : (
                    <p>{t('forms.noContent')}</p>
                  )}
                </div>
                <div className="responsive-flex gap-2">
                  {/* Images */}
                  <div className="responsive-flex gap-4 flex-col w-full">
                    <div className="responsive-flex gap-4 w-full flex-row flex-wrap justify-center">
                      <Gallery withDownloadButton>
                        {images?.map((file) => {
                          // const thumbnailUrl = ImageFromStrapiMedia(
                          //   file.formats.thumbnail
                          // ).uri;
                          const originalUrl = ImageFromStrapiMedia(file).uri;

                          return (
                            <Item
                              original={originalUrl}
                              thumbnail={originalUrl}
                              width={file.width}
                              height={file.height}
                              key={file.id}
                            >
                              {({ ref, open }) => (
                                <img
                                  alt={file.name}
                                  ref={ref as unknown as React.MutableRefObject<HTMLImageElement>}
                                  onClick={open}
                                  src={originalUrl}
                                  className="rounded-lg cursor-pointer object-cover hover:shadow-lg h-36 w-36 md:h-52 md:w-52 xl:h-64 xl:w-64"
                                />
                              )}
                            </Item>
                          );
                        })}
                      </Gallery>
                    </div>
                  </div>
                </div>
              </div>
              {/* Column 2 */}
              <div className="gap-2 flex-col w-full lg:basis-1/2">
                <div className="white-box responsive-flex flex-col w-full">
                  <ProfileCard
                    profile={authorOperator}
                    title={t('forms.author')}
                    onClick={handleAuthorClick}
                  />
                </div>
                <div className="white-box flex flex-col flex-1 gap-2 mt-2">
                  {/* documents */}
                  <div className="responsive-flex gap-4 flex-col w-full">
                    <div className="responsive-flex gap-2 w-full flex-col">
                      <SectionTitle
                        title={t('common.attachedDocuments')}
                        icon="AttachedFile"
                        className="text-fourth mt-2"
                      />
                      {documents?.map((doc) => {
                        const originalUrl = ImageFromStrapiMedia(doc).uri;

                        return (
                          <div key={doc.id} className="flex flex-row items-center w-full   gap-2">
                            <a
                              href={originalUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary flex-1 px-2 py-1 border border-stone-200 rounded bg-stone-50 hover:bg-stone-100 hover:border-stone-400"
                            >
                              {doc.name}
                            </a>
                            <button
                              className="p-1 rounded bg-stone-50 hover:bg-stone-100 transition-colors border border-stone-200 hover:border-stone-400 py-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = doc.url;
                                link.download = doc.name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <Icon name="DownloadFile" className="w-6 h-6" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentDetails;
